import { computed, watch } from "@vue/runtime-core";
import { XoneDataObject } from "./appData/core/XoneDataObject";
import AppDataHandler from "./AppDataHandler";
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';


class XoneViewsHandler {
	/**
	 * _instance
	 * @type {XoneViewsHandler}
	 */
	static _instance;

	/**
	 * views
	 * @type {Map<string, XoneView>}
	 */
	viewsMap = new Map();

	constructor() {
		if (XoneViewsHandler._instance) return XoneViewsHandler._instance;
		XoneViewsHandler._instance = this;
	}

	/**
	 * Add View
	 * @param {XoneView} xoneView
	 */
	addView(xoneView) {
		this.viewsMap.set(xoneView.getXoneDataObject().getHashId(), xoneView);
	}

	removeView(xoneView) {
		if (!this.viewsMap.has(xoneView.getXoneDataObject()?.getHashId() || "")) return;
		this.viewsMap.delete(xoneView.getXoneDataObject().getHashId());
	}

	/**
	 * Get View
	 * @param {XoneDataObject} xoneDataObject
	 * @returns {XoneView}
	 */
	getView(xoneDataObject) {
		return this.viewsMap.has(xoneDataObject.getHashId()) ? this.viewsMap.get(xoneDataObject.getHashId()) : null;
	}

	/**
	 * cleanRemovedViews
	 */
	cleanRemovedViews() {
		// Si el tamaño del viewmap excede de 500, vamos a hacer limpieza de todos los views que no se se encuentren directamente en el stack de breadcrumbs
		if (this.viewsMap.size <= 500) return;
		const xoneHashIds = computed(() => AppDataHandler.getBreadcrumbs().map((e) => e.xoneHashId));
		watch(
			() => xoneHashIds.value.length,
			() =>
				Array.from(this.viewsMap.keys()).forEach((e) => {
					if (!xoneHashIds.value.includes(e)) this.viewsMap.delete(e);
				})
		);
	}
}

/**
 * @type {XoneViewsHandler}
 */
export const xoneViewsHandler = new XoneViewsHandler();

// Monitorice and clean removed views
Promise.resolve().then(() => xoneViewsHandler.cleanRemovedViews());

export const getView = (/** @type {XoneDataObject} */ xoneDataObject) => xoneViewsHandler.getView(xoneDataObject);

export class XoneView {
	/**
	 * bindedEvents
	 * @type {Array<object>}
	 */
	_bindedEvents = [];

	/**
	 * @type {XoneDataObject}
	 */
	_xoneDataObject;

	/**
	 * constructor
	 * @param {XoneDataObject} xoneDataObject
	 */
	constructor(xoneDataObject) {
		this._xoneDataObject = xoneDataObject;
		if (xoneDataObject) xoneViewsHandler.addView(this);
	}

	get bindedEvents() {
		return this._bindedEvents;
	}

	/**
	 * addControl
	 * @param {XoneControl} control
	 */
	addControl(control) {
		this[control.name] = control;
	}

	getControl(name) {
		return this[name];
	}
    getExcelAlpha(col) {
        let s = "";
        while (col > 0) {
            let m = (col - 1) % 26;
            s = String.fromCharCode(65 + m) + s;
            col = Math.floor((col - m) / 26);
        }
        return s;
    }

async export (contents, type, /** @type {name:String} */ downloadName  = "data.csv", /** @type {templateName:String} */ templateName = null){
	
				// Helper to convert column index to Excel letter

				const csvProps = [];
				const csvPropText = [];

				/** @type {{m_xmlNode:XmlNode}} */
				const { m_xmlNode } = contents;

				m_xmlNode.SelectNodes("prop").forEach((/** @type {XmlNode} */ e) => {
					if (e.getAttrValue("export-csv") === "true") {
						csvPropText.push(e.getAttrValue("csv-title") || e.getAttrValue("title") || e.getAttrValue("name"));
						csvProps.push(e.getAttrValue("name"));
					}
				});

				var values =  await contents.loadAll(false, {
						start: -1,
						length: -1,
					});

				
				switch(type){
					case "csv":
						let csvText = csvPropText.map((e) => `"${e}"`).join("\t") + "\n";
						for (let i = 0; i < values.length; i++) {
							/** @type {XoneDataObject} */
							const objTmp = await values.get(i);
							csvProps.forEach((e) => (csvText += `${(objTmp[e] ?? "").toString()}\t`));
							csvText += " \n ";
						}
						const element = document.createElement("a");
						element.setAttribute("href", "data:text/plain;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csvText));
						element.setAttribute("download", downloadName );

						element.style.display = "none";
						document.body.appendChild(element);

						element.click();
						setTimeout(() => document.body.removeChild(element), 10000);
						break;
					case "xlsx":
						const workbook = new ExcelJS.Workbook();
						if (templateName) {
							// Load template from templates folder
							const templatePath = `templates/${templateName}`;
							try {
								const response = await fetch(templatePath);
								if (!response.ok) throw new Error(`Template not found: ${templatePath}`);
								const arrayBuffer = await response.arrayBuffer();
								await workbook.xlsx.load(arrayBuffer);
							} catch (err) {
								console.warn(`Failed to load template: ${err}. Creating new workbook.`);
							}
						}

						// Ensure first sheet exists
						let sheet;
						if (workbook.worksheets.length > 0) {
							sheet = workbook.worksheets[0];
						} else {
							sheet = workbook.addWorksheet(contents.getName());
						}

						// Get header row (first row) to copy formatting from
						const headerRow = sheet.getRow(1);

						// If no template, add headers in first row
						if (!templateName) {
							csvProps.forEach((key, colIndex) => {
								const cell = headerRow.getCell(colIndex + 1);
								cell.value = key;
								// Optionally set default styling for headers
								cell.font = { bold: true };
							});
						}

						// Insert data starting from row 2 (below header)
						let startRow = 2; // assuming row 1 has headers
						for (let i = 0; i < values.length; i++) {
							const objTmp = await values.get(i);
							const rowNum = startRow + i; // row 2 onwards
							const row = sheet.getRow(rowNum);

							csvProps.forEach((key, colIndex) => {
								const cell = row.getCell(colIndex + 1);
								cell.value = objTmp[key] ?? "";
							});
						}
						
						// Now add data validations from other sheets
							sheet.getRow(1).eachCell((headerCell, colIndex) => {
								const headerName = headerCell.value;
								if (!headerName) return;

								// Find a sheet with that name
								const refSheet = workbook.getWorksheet(headerName);
								if (!refSheet) return;

								// Get all values from first column of that sheet
								const listValues = [];
								refSheet.eachRow((r) => {
									const v = r.getCell(1).value;
									if (v !== null && v !== undefined && v !== "") listValues.push(v);
								});

								if (listValues.length === 0) return;
								const colLetter = this.getExcelAlpha(colIndex); // need a helper for column letter
								// Apply data validation to the whole column (from row 2 downwards)
								sheet.dataValidations.add(`${colLetter}2:${colLetter}1048576`, {
									type: "list",
									allowBlank: true,
                					formulae: [`"${listValues.join(",")}"`],
									showErrorMessage: true,
									errorStyle: "stop",
									errorTitle: "Invalid value",
									error: "Please select a value from the dropdown list.",
								});

							});

						// Export workbook as a downloadable file
						const buffer = await workbook.xlsx.writeBuffer();
						const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

						const link = document.createElement("a");
						link.href = URL.createObjectURL(blob);
						link.download = downloadName;
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
						URL.revokeObjectURL(link.href);
           			break;
					default:
						break;
				}
				
			};
			
	async exportCsv(contents, filename = "data.csv"){
		const csvProps = [];
		const csvPropText = [];

		/** @type {{m_xmlNode:XmlNode}} */
		const { m_xmlNode } = contents;

		m_xmlNode.SelectNodes("prop").forEach((/** @type {XmlNode} */ e) => {
			if (e.getAttrValue("export-csv") === "true") {
				csvPropText.push(e.getAttrValue("csv-title") || e.getAttrValue("title") || e.getAttrValue("name"));
				csvProps.push(e.getAttrValue("name"));
			}
		});
		let csvText = csvPropText.map((e) => `"${e}"`).join("\t") + "\n";

		for (let i = 0; i < contents.length; i++) {
			/** @type {XoneDataObject} */
			const objTmp = await contents.get(i);
			csvProps.forEach((e) => (csvText += `${(objTmp[e] ?? "").toString()}\t`));
			csvText += " \n ";
		}
		const element = document.createElement("a");
		element.setAttribute("href", "data:text/plain;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csvText));
		element.setAttribute("download", filename);

		element.style.display = "none";
		document.body.appendChild(element);

		element.click();
		setTimeout(() => document.body.removeChild(element), 10000);
	}

	setXoneDataObject(dataObject) {
		this._xoneDataObject = dataObject;
	}

	getXoneDataObject() {
		return this._xoneDataObject;
	}

	/**
	 * Bind event
	 * @param  {...string} Args
	 */
	bind(...Args) {
		if (!Args || Args.length < 3) return console.error("Error calling bind method. You need 3 arguments at least.");

		let bindedEvent = this._bindedEvents.find((e) => e.field === Args[0] && e.eventName === Args[1].toString().toLowerCase());

		if (!bindedEvent)
			bindedEvent = {
				fieldName: Args[0],
				eventName: Args[1].toString().toLowerCase(),
			};

		if (Args.length === 3) bindedEvent.action = Args[2];
		else {
			bindedEvent.params = Args[2];
			bindedEvent.action = Args[3];
		}

		// Add event bind
		this._bindedEvents.push(bindedEvent);
	}

	/**
	 * exit current view
	 */
	exit() {
		AppDataHandler.clearBreadcrumbsFromXoneHashId(this._xoneDataObject["_XoneHashId"]);
	}

	/**
	 * Refresh
	 * @param  {...any} Props
	 */
	refresh(...Props) {
		const props = Props.length === 1 ? Props[0].toString().split(",") : Props;
		props.forEach((/** @type {*} */ e) => {
			if (this[e]?.refresh) this[e].refresh();
		});
		if (Props.length === 0)
			Object.values(this).forEach((e) => {
				if (!e) return;
				if (e.refresh) e.refresh();
			});
	}

	/**
	 * Refresh All
	 */
	refreshAll() {
		this.refresh();
	}

	/**
	 * refreshValue
	 * @param {*} Props
	 */
	refreshValue = (Props) => {
		this.refresh(Props);
	};
	  /**
     * setFocus
     * @param {string} propName
     */
	  setFocus = (propName) => {
        const nextProp = document.querySelector(`[xone-name="${propName}"]`)
        if(!nextProp) return;
        nextProp.querySelector('input, button')?.focus();
    };
}

export class XoneControl {
	/**
	 * @type {string}
	 */
	name;

	/**
	 * @type {HTMLElement}
	 */
	element;

	refresh = () => {};

	refreshValue = () => {};

	/**
	 * constructor
	 * @param {string} name
	 * @param {HTMLElement} [element]
	 */
	constructor(name, element = null) {
		this.name = name;

		while (element && !element.classList.contains("xone-prop") && !element.classList.contains("xone-frame")) element = element.parentElement;

		this.element = element;
	}
}
